/*
 * ADAPTER CONTRACT for the shared policy screens (/pam/policies/:product).
 *
 * One list page, one detail page and one editor page serve all five products
 * (specs: policies-ui-spec.md, superseded in part by policy-form-advanced-spec.md).
 * Everything product-specific lives in ONE file per product,
 * src/pages/policies/products/<key>.jsx, whose default export is
 * `defineAdapter({...})`. The shell (this folder, outside products/) has no
 * product code. products/ad.jsx is the reference implementation.
 *
 * The editor is the same page on every product: ONE card (Policy name, Who,
 * Where, Action), the collapsible Advanced block (Conditions, Except, Use as
 * fallback, and on SSH Approval and Session), the one-line summary, and
 * [Cancel] [Create policy | Update policy]. Only Where differs.
 *
 * Registering: productRegistry.js already imports every products/<key> file.
 *
 * ─── The draft ────────────────────────────────────────────────────────────
 * The editor holds one plain object. The shell owns everything but `product`
 * and `who`'s field lists, and reads/writes its parts of policyJson itself:
 *
 *   {
 *     policyName: string,
 *     who:     { everyone: boolean, [field.key]: string[] }   // field keys from who.fields
 *     except:  { users: string[], groups: string[] }  // <-> conditions.identity.excludeUsers / excludeGroups
 *     conditions: object | undefined                  // policyJson.conditions WITHOUT identity.exclude*
 *     action:  "allow" | "mfa_required" | "block"      // <-> policyJson.policyFlow
 *     fallback: boolean                                // <-> policyJson.fallback
 *     approval: { required, approvers[], validMinutes, approverGroups[], requireJustification, requireTicket }
 *     session:  { record, maxDurationMinutes, idleTimeoutMinutes, reauthIntervalMinutes }
 *     product: { ...anything the adapter needs }       // adapter-owned
 *   }
 *
 * approval/session are only read and written when features.approval /
 * features.session are on (SSH; Database). features.session is true (every
 * control) or { record, reauth } (Database: max length and idle only).
 * Elsewhere they are absent from the form, and policyJson.actions is not sent
 * on an edit (the stored value is kept).
 *
 * ─── Saving (spec 6) ──────────────────────────────────────────────────────
 * policyJson = shellOverlay(adapter.toPolicyJson(draft, stored), draft, stored, { isEdit })
 *   - adapter.toPolicyJson MUST start from `stored` ({...stored, ...}) and
 *     overwrite only the keys its sections own. stored is {} for a new policy
 *     and the source policy's policyJson for a duplicate.
 *   - the shell then sets policyFlow, conditions (normalised, unknown sections
 *     kept, exclusions merged in), fallback (when features.fallback) and
 *     actions (SSH). The update API merges top-level keys onto the stored
 *     policy, so on an edit the shell sends conditions: null, fallback: false
 *     and (SSH) actions: null to clear them.
 *
 * ─── Fields ───────────────────────────────────────────────────────────────
 * key          "ad" | "ssh" | "radius" | "database" | "apps" (matches products/<key>)
 * policyType   the policy service's policyType ("AD", "radius", "app", ...)
 * modeKey      product switcher mode ("mfa_ad", "mfa_pam", ...)
 * labels: {
 *   product          "AD"      -> "Create AD policy", "Edit AD policy"
 *   eyebrow          "Identity · Policies" (list header eyebrow)
 *   listDescription  one line under the list title
 *   emptySentence    "Without a policy, sign-ins are allowed."
 *   subject          "sign-in" | "session" | "login" (used in shared copy)
 * }
 *
 * Data (each returns a Promise; reject with policyFailure(...), see below):
 *   list()        -> raw rows (this product only). ListPolicy ignores a
 *                    top-level policyType: send listFilters(policyType) and
 *                    keep a client-side filter as a safety net.
 *   get(id)       -> row | null. Default: list() + find.
 *   create({ policyName, policyJson, force }) -> { id?, warning? }
 *        force: true re-sends after a "conflict" failure ("Create anyway").
 *   update({ id, policyName, policyJson, stored })  stored = the loaded row
 *                 -> { id?, warning? }  id = the NEW id (every update writes a
 *                    new version). updatedPolicyId(res, id) reads it and
 *                    rejects a 404/500 hidden in a 200 body.
 *   setActive(row, active: boolean)          -> any (suspend / resume)
 *   remove(row)                              -> any
 *   rowId(row)        default row.id ?? row.policyId
 *   isActive(row)     default: status not Suspended / Inactive / Disabled
 *
 * Model:
 *   emptyDraft()      -> { who, product, action? }  for a new policy
 *   fromPolicy(row)   -> { who, product, conditions? }
 *        conditions: only when the adapter migrates stored data into
 *        conditions (AD: ad.sources -> network.include); otherwise the
 *        shell reads policyJson.conditions itself.
 *   toPolicyJson(draft, stored) -> policyJson (merged over stored, see above)
 *   actionOf(policyJson)  -> flow. Default: policyShell flowOf (mfa/deny folded, else allow).
 *   validate(draft, data) -> { "<section>.<field>": "Plain sentence." }
 *        section is basics | who | where | action. The shell shows a message
 *        once its field was touched (touch(key) on blur) or after a save
 *        attempt, and scrolls to the first one. Name, Who-empty, Conditions,
 *        Approval and Session are validated by the shell.
 *
 * Sections (components get SectionProps):
 *   SectionProps = { draft, product: draft.product, setProduct(patch, { silent }?),
 *                    errors (visible errors, keyed as validate()), touch(key),
 *                    data (useData's result), flags (policyFlags), isEdit }
 *        setProduct(patch, { silent: true }) also moves the "unsaved" baseline:
 *        use it for defaults filled in after loading (e.g. the first domain).
 *   Where       required; the Where sub-section (spec 4): Domain / Directory /
 *               Gateway first, then the product's fields.
 *   useData(draft, setProduct) optional hook, called once at the top of the
 *               list, detail and editor pages (draft is null on list/detail).
 *   who: {
 *     everyone: boolean       offer "Everyone" (draft.who.everyone)
 *     except:   boolean       offer "Except" under Advanced (needs fields with `except`)
 *     fields: [{
 *       key: "users",         draft.who.users
 *       label: "Users",
 *       kind?: "users" | "groups" | "ous"   how the summary names a value (default: key)
 *       placeholder: "Search AD users",
 *       search(query, { draft, data }) -> Promise<[{ value, label?, sub? }]>
 *       deps(draft)?          -> array; options reload when it changes
 *       disabledReason(draft, data)? -> "Pick a domain first" | null
 *       single?: boolean      one value only (Apps)
 *       except?: "users" | "groups"   also searchable in Except, written there
 *       noun?: "OU"           singular for the "Pick at least one ..." message
 *       Picker?: Component({ value, onChange, draft, data, disabled })  instead of search
 *     }]
 *   }
 *
 * Words:
 *   listWhere(row)          -> string  ("Kerberos, LDAP", "web-01 as ubuntu")
 *   listWho(row)?           -> { everyone, names[] }. Default: from fromPolicy(row).who.
 *   sentenceWhere(draft, data) -> the summary sentence's Where phrase
 *                              ("on dbmfa_test as dbmfa_reader"), or "".
 *   reviewRows(draft, data) -> [{ key, label, value, section: "where" | "action" }]
 *        the detail page's rows: "where" rows follow Who, "action" rows follow Action.
 *
 * features: {
 *   fallback: boolean                 "Use as fallback" under Advanced (not Apps)
 *   approval: false | { search(query, ctx) -> Promise<Option[]> }  SSH only
 *   session: boolean                  SSH only
 *   impact?: (draft, data) -> Promise<string>    "Count matching users" under the summary (AD)
 *   TestSignIn?: Component({ draft, data })      "Test a sign-in" drawer under the summary (AD)
 *   ActionExtra?: Component(SectionProps & { action })  under the action's line (AD MFA prompt)
 *   actionSentences?: { allow?, mfa_required?, block? }  override the line under the control
 *   mfaWinsOverAllow?: boolean        Database: the list's Allow header says so
 *   specificity?: (item) => number    engine order inside a list group, higher first
 *   ListHeaderExtra?: Component({ reload, data })  extra header button(s) on the list
 *   privilegeHint?: string            Privilege condition's meaning (default signals PRIVILEGE_MEANING[key])
 *   networkNote?: string              one line in the Network condition (AD)
 * }
 *
 * ─── Failures ─────────────────────────────────────────────────────────────
 * Reject data calls with policyFailure(errOrBody, kind?):
 *   { kind: "invalid", reason }   a 400 "Invalid policy: <reason>" (detected for you)
 *   { kind: "conflict", conflicts: row[], warning }   create refused (409)
 *   { kind: "gone" }              update: the policy was deleted or replaced
 *   { kind: "failed" }            anything else
 * The shell never shows reason or raw text: it maps known reasons to a plain
 * sentence (policyShell friendlyPolicyError) and logs the raw value.
 * callService(fn, payload) wraps the repo's ({ payload, onSuccess, onFailure,
 * onConflict }) service style into a Promise with these failures.
 */
import { policyErrorMessage } from "../../components/policyShell/conditionsModel";
import { flowOf } from "../../components/policyShell/policyModel";

const INACTIVE = ["suspended", "inactive", "disabled", "paused"];

/** A rejected data call, normalised. */
export function policyFailure(raw, kind) {
  if (raw && raw.__policyFailure) return raw;
  const reason = policyErrorMessage(raw);
  const k = kind || (reason ? "invalid" : "failed");
  return {
    __policyFailure: true,
    kind: k,
    reason: reason || null,
    conflicts: raw?.conflicts || [],
    warning: raw?.warning || "",
    raw,
  };
}

/**
 * Promise wrapper for this repo's callback services.
 *   callService(listADPolicies, payload) -> resolves with the response body
 * onConflict (where the service has one) rejects with kind "conflict".
 */
export function callService(fn, payload) {
  return new Promise((resolve, reject) => {
    fn({
      payload,
      onSuccess: resolve,
      onFailure: (e) => reject(policyFailure(e)),
      onConflict: (c) => reject(policyFailure({ conflicts: c?.conflicts, warning: c?.warning, raw: c?.raw }, "conflict")),
    });
  });
}

/** ListPolicy's filter for one policy type (a top-level policyType is ignored). */
export const listFilters = (policyType) => [{ filterParameter: "policyType", filterValue: policyType }];

/**
 * The id an update answered with: updatedPolicyID.data (or a bare string, as
 * the SSH endpoint sends). The service answers HTTP 200 with
 * updatedPolicyID.code 404 / 500 when the policy is gone or no longer the
 * live version: that rejects as kind "gone".
 */
export function updatedPolicyId(res, fallbackId) {
  const u = res?.updatedPolicyID;
  if (typeof u === "string") return u || fallbackId;
  if (u && typeof u === "object") {
    const code = Number(u.code);
    if (u.code !== undefined && !(code >= 200 && code < 300)) throw policyFailure(u, "gone");
    return u.data || fallbackId;
  }
  return fallbackId;
}

const REQUIRED = ["key", "policyType", "list", "create", "update", "setActive", "remove", "emptyDraft", "fromPolicy", "toPolicyJson", "Where", "listWhere", "reviewRows"];

/** Problems with an adapter's shape ([] when it is complete). Used by tests. */
export function adapterProblems(a) {
  if (!a) return ["adapter is null"];
  const out = REQUIRED.filter((k) => a[k] === undefined || a[k] === null).map((k) => `missing ${k}`);
  if (!a.labels?.product) out.push("missing labels.product");
  if (!a.labels?.subject) out.push("missing labels.subject");
  if (!a.labels?.emptySentence) out.push("missing labels.emptySentence");
  if (!Array.isArray(a.who?.fields) || !a.who.fields.length) out.push("missing who.fields");
  (a.who?.fields || []).forEach((f, i) => {
    if (!f.key || !f.label || (typeof f.search !== "function" && !f.Picker)) out.push(`who.fields[${i}] needs key, label and search`);
  });
  return out;
}

/** Fills the contract's defaults. Every products/<key> file exports defineAdapter({...}). */
export function defineAdapter(spec) {
  const rowId = spec.rowId || ((row) => row?.id ?? row?.policyId ?? null);
  const adapter = {
    labels: {},
    who: { everyone: true, except: true, fields: [] },
    validate: () => ({}),
    actionOf: flowOf,
    sentenceWhere: () => "",
    isActive: (row) => !INACTIVE.includes(String(row?.status || "").toLowerCase()),
    ...spec,
    rowId,
    features: {
      fallback: false,
      approval: false,
      session: false,
      ...(spec.features || {}),
    },
  };
  if (!spec.get) {
    adapter.get = async (id) => {
      const rows = await adapter.list();
      return rows.find((r) => String(rowId(r)) === String(id)) || null;
    };
  }
  if (!spec.listWho) {
    adapter.listWho = (row) => {
      const { who } = adapter.fromPolicy(row);
      const names = (adapter.who.fields || []).flatMap((f) => who?.[f.key] || []);
      return { everyone: !!who?.everyone, names };
    };
  }
  return adapter;
}
